import React, {useEffect, useState, useMemo, useRef} from 'react';
import '../../styles/PageCommon.css';
import './SupplyChainPage.css';
import { ReactComponent as DownloadIcon } from '../../assets/icons/download-icon.svg';
import { ReactComponent as FiltersIcon } from '../../assets/icons/filters-icon.svg';
import { ReactComponent as LoadingIcon } from '../../assets/icons/upload-loading-icon.svg';
import EditableCell from '../../components/common/EditableCell';
import { supplyChainService } from '../../services/supplyChainService';
import { numberToKiloFormat } from '../../utils/utils';
import LoadingAnimation from '../../components/common/LoadingAnimation/LoadingAnimation';
import { parseApiError } from '../../utils/parseErrorMessage';
import { NotificationContainer } from '../../components/common/NotificationContainer';
import NotificationModal from '../../components/common/NotificationModal';
import MonthYearPicker from '../../components/common/MonthYearPicker';
import Pagination from '../../components/common/Pagination/Pagination';
import { useFilterParams } from '../../hooks/useFilterParams';
import MultiSelectDropdown from '../../components/common/MultiSelectDropdown';
import ColumnFilterDropdown from '../../components/common/ColumnFilterDropdown';
import InfoTooltip from '../../components/common/InfoTooltip/InfoTooltip';
import SegmentedControl from '../../components/common/SegmentedControl/SegmentedControl';

const COLUMNS = [
  { key: 'sku_code',                      label: 'Код СКЮ' },
  { key: 'sku_name',                      label: 'Наименование товара' },
  { key: 'month_prior_available_stock',   label: 'Наличие товара -1 от планируемого месяца' },
  { key: 'average_l3m_quantity_in_mc',    label: 'Средние продажи за последние 3 мес' },
  { key: 'average_f3m_quantity_in_mc',    label: 'Средние продажи за будущие 3 мес' },
  { key: 'recommended_quantity_in_mc',    label: 'Рекомендуемое кол-во' },
  { key: 'adjusted_quantity_in_mc',       label: 'Заказать в кол-ве' },
];

const DEFAULT_FILTERS = {
  page: 1,
  page_size: 10,
  source: [],
  category: [],
  view_type: null,
  ...Object.fromEntries(COLUMNS.map(({ key }) => [key, []])),
};

export default function SupplyChainPage() {
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [supplyChainTotalItems, setSupplyChainTotalItems] = useState(0);
  const [supplyChainData, setSupplyChainData] = useState([]);
  const [supplyChainSummary, setSupplyChainSummary] = useState({});
  const [isUpdated, setIsUpdated] = useState(false);
  const [supplyChainError, setSupplyChainError] = useState("")
  const [headers, setHeaders] = useState({});
  const [infoIcons, setInfoIcons] = useState({});
  const [currentMonth, setCurrentMonth] = useState(null)

  const [editingSupplyChainRows, setEditingSupplyChainRows] = useState({});

  const supplyChainCellKey = (row, index) => `${row.sku_code}_${index}`;

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isFiltersMenuOpen, setIsFiltersMenuOpen] = useState(false);
  const filtersMenuRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isFiltersMenuOpen) return;
    const handleClickOutside = (e) => {
      // Inside the filters menu itself — keep open.
      if (filtersMenuRef.current && filtersMenuRef.current.contains(e.target)) return;
      // Inside a portaled dropdown menu (rendered on document.body, so outside
      // filtersMenuRef) — keep open; that dropdown manages its own closing.
      if (e.target.closest && e.target.closest('.dropdown-portal')) return;
      setIsFiltersMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isFiltersMenuOpen]);

  const handleResetFilters = () => {
    updateFilters({ category: [], source: [], view_type: null });
    setIsFiltersMenuOpen(false);
  };

  // --- Filters State and Logic ---
  const [categories, setCategories] = useState([]);
  const [sources, setSources] = useState([]);
  const [filterOptions, setFilterOptions] = useState({});
  const { filters, updateFilters } = useFilterParams(DEFAULT_FILTERS);

  const colOptions = useMemo(() => {
    const result = {};
    Object.entries(filterOptions).forEach(([key, values]) => {
      result[key] = (values || []).map(String);
    });
    return result;
  }, [filterOptions]);

  const handleColFilterApply = (key, vals) => updateFilters({ [key]: vals, page: 1 });
  const getColSelected = (key) => filters[key] || [];

  // Table + summary data — re-fetches on any filter change.
  useEffect(() => {
    let cancelled = false;
    setIsLoadingData(true);
    setSupplyChainError("");
    const columnFilters = Object.fromEntries(
      COLUMNS.map(({ key }) => [key, filters[key]]).filter(([, v]) => v?.length)
    );
    Promise.allSettled([
      supplyChainService.getData(filters.category, filters.source, filters.page, filters.page_size, columnFilters),
      supplyChainService.getSummary(filters.category, filters.source, filters.view_type),
      supplyChainService.getInfoIcons(),
      supplyChainService.getFilterOptions(),
    ]).then(([dataRes, summaryRes, infoIconsRes, filterOptionsRes]) => {
      if (cancelled) return;

      const errors = [];

      if (dataRes.status === 'fulfilled') {
        const items = dataRes.value?.items;
        if (items?.length) {
          setSupplyChainData(items);
          setSupplyChainTotalItems(dataRes.value.total_items ?? items.length);
        } else {
          setSupplyChainData([]);
          setSupplyChainTotalItems(0);
        }
        if (dataRes.value?.filter_options) setFilterOptions(dataRes.value.filter_options);
        if (dataRes.value?.headers) setHeaders(dataRes.value.headers);
        if (dataRes.value?.current_month) setCurrentMonth(dataRes.value.current_month);
      } else {
        setSupplyChainData([]);
        setSupplyChainTotalItems(0);
        const msg = parseApiError(dataRes.reason);
        if (msg) errors.push(msg);
      }

      if (summaryRes.status === 'fulfilled' && summaryRes.value) {
        setSupplyChainSummary(summaryRes.value);
      } else if (summaryRes.status === 'rejected') {
        const msg = parseApiError(summaryRes.reason);
        if (msg) errors.push(msg);
      }

      if (infoIconsRes.status === 'fulfilled' && infoIconsRes.value) {
        setInfoIcons(infoIconsRes.value);
      } else if (infoIconsRes.status === 'rejected') {
        const msg = parseApiError(infoIconsRes.reason);
        if (msg) errors.push(msg);
      }

      if (filterOptionsRes.status === 'fulfilled' && filterOptionsRes.value) {
        if (filterOptionsRes.value.categories) setCategories(filterOptionsRes.value.categories);
        if (filterOptionsRes.value.sources) setSources(filterOptionsRes.value.sources);
      } else if (filterOptionsRes.status === 'rejected') {
        const msg = parseApiError(filterOptionsRes.reason);
        if (msg) errors.push(msg);
      }

      if (errors.length) setSupplyChainError(errors.join("\n"));
    }).finally(() => { if (!cancelled) setIsLoadingData(false); });
    return () => { cancelled = true; };
  }, [filters]);

  const handleUpdateAdjustedQty = (sku_code, adjusted_quantity_in_mc) => {
    supplyChainService.updateAdjustedQty(currentMonth, sku_code, adjusted_quantity_in_mc)
      .then(response => {
        console.log("supplyChainService.updateAdjustedQty response ", response)
        if (response?.rows_updated) {
          setIsUpdated(true);
        }
      })
      .catch(error => {
        console.log("supplyChainService.updateAdjustedQty error ", error)
        const errorMessage = parseApiError(error)
        setSupplyChainError(errorMessage ? errorMessage : "");
      })
;
  }

  const handleDownloadExcel = async () => {
    setDownloading(true);
    try {
      const response = await supplyChainService.downloadExcel(
        currentMonth,
        filters.source,
        filters.category
      );

      // Create blob URL
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });

      const url = window.URL.createObjectURL(blob);

      // Extract filename from header (if exists)
      const contentDisposition = response.headers["content-disposition"];
      let filename = "download.xlsx";

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match?.[1]) filename = match[1];
      }

      // Create temp link
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;

      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed", error);
    }
    setDownloading(false);
  };


  return (
    <>
      {isLoadingData && <LoadingAnimation />}
      <div className="page-content">
        <NotificationContainer>
          {
            isUpdated && <NotificationModal closeModal={() => setIsUpdated(false)} type="success" title="Успешно обновлен" duration={2000} />
          }
          {
            supplyChainError && <NotificationModal closeModal={() => setSupplyChainError(null)} type="error" subTitle={supplyChainError} title="Ошибка" duration={3000}/>
          }
        </NotificationContainer>
        <div className="supply-chain-header-container">
            <h1 className="supply-chain-title">Заказ</h1>
            <div className='supply-chain-header-buttons'>
              <button type="button" className="primary-button" onClick={handleDownloadExcel}>
                <DownloadIcon size={24} color="#FFFFFF"/>
                {/* Cкачать Excel */}
                {
                  downloading
                    ? <LoadingIcon size={20} color="#ffffff" className={"loading-icon spinning"}/>
                    : "Cкачать Excel"
                }

              </button>
            </div>
        </div>

        <div className="supply-chain-sub-header-container">
          <div className="supply-chain-stats-container">
              <div className='supply-chain-stats-card stats-total-number'>
                Сумма: <span className='supply-chain-stats-card-value'>{numberToKiloFormat(supplyChainSummary.total_sum, { kiloThreshold: 10_000 })}</span>
              </div>
              <div className='supply-chain-stats-card stats-total-cases'>
                Кол-во короб: <span className='supply-chain-stats-card-value'>{numberToKiloFormat(supplyChainSummary.total_quantity, { kiloThreshold: 10_000 })}</span>
              </div>
              <div className='supply-chain-stats-card stats-total-weight'>
                Вес брутто: <span className='supply-chain-stats-card-value'>{numberToKiloFormat(supplyChainSummary.total_gross_weight, { kiloThreshold: 10_000 })}</span>
              </div>
              <div className='supply-chain-stats-card stats-total-cbn'>
                Объем м3: <span className='supply-chain-stats-card-value'>{numberToKiloFormat(supplyChainSummary.total_volume, { kiloThreshold: 10_000 })}</span>
              </div>
          </div>
          {windowWidth >= 2071 ? (
            <div className="supply-chain-filters">
              <MultiSelectDropdown
                label="Категория"
                placeholder="Все"
                selectedOptions={filters.category}
                options={categories}
                onChange={(values) => updateFilters({ category: values })}
                filterSelectStyle={{ flex: 1, display: "block" }}
              />
              <MultiSelectDropdown
                label="Источник"
                placeholder="Все"
                selectedOptions={filters.source}
                options={sources}
                onChange={(values) => updateFilters({ source: values })}
                filterSelectStyle={{ flex: 1, display: "block" }}
              />
              <SegmentedControl
                value={filters.view_type?.toLowerCase() ?? "dsp"}
                onChange={(value) => updateFilters({ view_type: value })}
                options={[
                  { label: "Прайс-лист", value: "dsp" },
                  { label: "Инвойс цена", value: "invoice price" },
                ]}
              />
              <MonthYearPicker
                value={currentMonth}
                placeholder=""
                readOnly={true}
              />
            </div>
          ) : (
            <div className="supply-chain-compact-filters">
              <div className="filters-menu-wrapper" ref={filtersMenuRef}>
                <button
                  className="filters-icon-btn"
                  onClick={() => setIsFiltersMenuOpen(p => !p)}
                >
                  <FiltersIcon color='rgba(137, 137, 137, 1)'/>
                </button>
                {(filters.category.length > 0 || filters.source.length > 0) && (
                  <span className="filters-active-badge">
                    {(filters.category.length > 0 ? 1 : 0) + (filters.source.length > 0 ? 1 : 0)}
                  </span>
                )}
                {isFiltersMenuOpen && (
                  <div className="filters-dropdown-menu">
                    <span className="filters-dropdown-title">Фильтровать:</span>
                    <MultiSelectDropdown
                      label="Категория"
                      placeholder="Все"
                      selectedOptions={filters.category}
                      options={categories}
                      onChange={(values) => updateFilters({ category: values })}
                    />
                    <MultiSelectDropdown
                      label="Источник"
                      placeholder="Все"
                      selectedOptions={filters.source}
                      options={sources}
                      onChange={(values) => updateFilters({ source: values })}
                    />
                    <SegmentedControl
                      value={filters.view_type?.toLowerCase() ?? "dsp"}
                      onChange={(value) => updateFilters({ view_type: value })}
                      options={[
                        { label: "Прайс-лист", value: "dsp" },
                        { label: "Инвойс цена", value: "invoice price" },
                      ]}
                    />
                    <button className="cancel-button" onClick={handleResetFilters}
                      style={{background: "rgba(124, 200, 5, 0.07)", border: "1px solid rgba(124, 200, 5, 1)"}}>
                      Сбросить
                    </button>
                  </div>
                )}
              </div>
              <MonthYearPicker
                value={currentMonth}
                placeholder=""
                isCalendarIcon={true}
                readOnly={true}
              />
            </div>
          )}
        </div>


        <div className="data-table-container">
          <table className="data-table supply-chain-table">
            <thead>
              <tr>
                {COLUMNS.map(({ key, label: defaultLabel }) => {
                  const label = headers[key] || defaultLabel;
                  const tooltipText = infoIcons[key];
                  const hasFilter = key in filterOptions;
                  return (
                    <th key={key}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6,  }}>
                        <span>{label}</span>
                        {tooltipText && <InfoTooltip>{tooltipText}</InfoTooltip>}
                        {hasFilter && (
                          <ColumnFilterDropdown
                            label={label}
                            options={colOptions[key] || []}
                            selected={getColSelected(key)}
                            onApply={(vals) => handleColFilterApply(key, vals)}
                          />
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {
                (supplyChainData && supplyChainData.length > 0) ? supplyChainData.map((row, mainIndex) => {
                  const key = supplyChainCellKey(row, mainIndex);
                  const isSupplyChainRowEdit = editingSupplyChainRows[key] !== undefined && editingSupplyChainRows[key] !== false;
                
                  return(
                    <tr key={mainIndex}>
                      <td>
                        <div className="supply-chain-table-column-number">{row.sku_code}</div>
                      </td>
                      <td>
                        <div className="supply-chain-table-column-created-date">{row.sku_name}</div>
                      </td>
                      <td>
                        <div className="supply-chain-table-column-arrival-date">{row.month_prior_available_stock?.toLocaleString("ru-RU")}</div>
                      </td>
                      <td>
                        <div className="supply-chain-table-column-box-sum">{row.average_l3m_quantity_in_mc?.toLocaleString("ru-RU")}</div>
                      </td>
                      <td>
                        <div className="supply-chain-table-column-amount">{row.average_f3m_quantity_in_mc?.toLocaleString("ru-RU")}</div>
                      </td>
                      <td>
                        <div className="supply-chain-table-column-amount">{row.recommended_quantity_in_mc?.toLocaleString("ru-RU")}</div>
                      </td>
                      <td style={{minWidth: "170px"}}>
                        <EditableCell
                          value={row.adjusted_quantity_in_mc}
                          isEditing={isSupplyChainRowEdit}
                          onChange={(e) => setSupplyChainData(prev =>
                            prev.map((item, index) =>
                              index === mainIndex
                                ? { ...item, adjusted_quantity_in_mc: e.target.value }
                                : item
                            )
                          )}
                          onEdit={() => setEditingSupplyChainRows(prev => ({ ...prev, [key]: row.adjusted_quantity_in_mc }))}
                          onConfirm={() => {
                            handleUpdateAdjustedQty(row.sku_code, row.adjusted_quantity_in_mc);
                            setEditingSupplyChainRows(prev => ({ ...prev, [key]: false }));
                          }}
                          onCancel={() => {
                            setSupplyChainData(prev => prev.map((item, i) =>
                              i === mainIndex ? { ...item, adjusted_quantity_in_mc: editingSupplyChainRows[key] } : item
                            ));
                            setEditingSupplyChainRows(prev => ({ ...prev, [key]: false }));
                          }}
                          renderValue={(v) => v?.toLocaleString("ru-RU")}
                          editingContainerStyle={{width: "auto"}}
                          displayContainerStyle={{maxWidth: "130px"}}
                        />
                      </td>
                    </tr>
                  )
              }) : <tr key={"supplyChainData-1"} >
                        <td style={{margin: "auto", textAlign: 'center'}} colSpan="7">Нет данных</td>
                    </tr>
              }
            </tbody>
          </table>
          <Pagination
            currentPage={filters.page}
            totalItems={supplyChainTotalItems}
            onPageChange={(p) => updateFilters({page: p})}
            rowsPerPage={filters.page_size}
            onRowsPerPageChange={(r) => { updateFilters({page_size: r}) }}
            rowsPerPageOptions={[5, 10, 20, 50]}
          />
        </div>
      </div>
    </>
  );
}

